import { Button, ConfirmModal, MoreButton, Space } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { useComment, useDeleteComment } from '@/features/comment/hooks/comment';
import { useNavigateToPinpoint } from '@/features/comment/hooks/navigate-to-pinpoint';
import { useCommentEditPermission } from '@/features/comment/hooks/permission';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import {
  type activeKeyQueryKeys,
  shareBySheetKeys,
} from '@/features/project/_utils/search-query-states';
import { useMessage } from '@/providers';
import { matchProjectDetailPath } from '@/utils/route-match';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';
import invariant from 'tiny-invariant';
import type z from 'zod';
import { CommentChangeReadStatusButton } from '../../common/CommentChangeReadStatusButton';
import { CommentResolveSwitch } from '../../common/CommentResolveSwitch';
import { usePinpoint } from '../../pinpoint';
import { CommentPublicationModal } from './CommentPublicationModal';

export const CommentDetailsHeader: FC = () => {
  const { t } = useTranslation();

  const stackSiderView = useCommentStackSiderView();
  const { resetPinpoint } = usePinpoint();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(siderConfig?.viewType === 'comment-details', t('unreachable-code'));

  const navigateToPinpoint = useNavigateToPinpoint();
  const location = useLocation();
  const { openMessage } = useMessage();
  const comment = useComment(siderConfig.id);
  const { data: me } = useMe();
  const isCommentAuthor = useCommentEditPermission(comment.data?.authorId);
  const { mutateAsync: deleteComment } = useDeleteComment({
    target: {
      key: 'project',
      id:
        stackSiderView.target.type === 'project'
          ? stackSiderView.target.projectId
          : '',
    },
  });

  const [openPublicationModalType, setOpenPublicationModalType] =
    useState<boolean>(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null,
  );

  const handleCloseArchiveModal = () => {
    setOpenPublicationModalType(false);
  };

  const handleCloseDeleteModal = () => {
    setDeletingCommentId(null);
  };

  const isObjectExist = !!comment.data?.objectName;

  const [searchParams] = useSearchParams();
  const activeKey = searchParams.get(
    shareBySheetKeys.Enum.activeKey,
  ) as z.infer<typeof activeKeyQueryKeys>;
  const isProjectPage = matchProjectDetailPath(location.pathname);

  // コメントのメール機能から遷移した場合、アクティブキーが設定されていないためcommentを見てページを判定する。
  // navigateToPinpointでredirectすることで、ピンポイントの位置までスクロールする。
  useEffect(() => {
    if (isProjectPage && !activeKey && comment.data?.pinpoint) {
      navigateToPinpoint({
        pinpoint: comment.data.pinpoint,
        objectId: comment.data.objectId,
        commentId: comment.data.id,
      });
    }
  }, [
    activeKey,
    comment.data?.pinpoint,
    comment.data?.objectId,
    comment.data?.id,
    isProjectPage,
    navigateToPinpoint,
  ]);

  if (!comment.data) {
    return null;
  }

  const hasEditPermission =
    isCommentAuthor || comment.data?.assigneeIds?.includes(me?.id ?? '');

  return (
    <>
      <Space>
        {
          <>
            {location.pathname.startsWith('/comment') &&
              comment.data.key === 'project' &&
              isObjectExist && (
                <Button
                  type="link"
                  size="small"
                  onClick={async () => {
                    if (!comment.data) {
                      return;
                    }
                    navigateToPinpoint({
                      pinpoint: comment.data.pinpoint,
                      objectId: comment.data.objectId,
                      commentId: comment.data.id,
                    });
                  }}
                >
                  {t('navigate-to-project')}
                </Button>
              )}
            <CommentResolveSwitch
              commentId={siderConfig.id}
              resolved={comment.data.resolved}
              disabled={!hasEditPermission}
            />
            <CommentChangeReadStatusButton
              commentId={siderConfig.id}
              readStatus={comment.data.readStatus}
            />
          </>
        }
        {comment.data.publishType === 'strict' && hasEditPermission && (
          <Button onClick={() => setOpenPublicationModalType(true)}>
            {t('sharing-settings')}
          </Button>
        )}
        {hasEditPermission && (
          <MoreButton
            items={[
              {
                key: 'edit',
                label: t('edit'),
                onClick: () => {
                  stackSiderView.push({
                    payload: {
                      config: {
                        viewType: 'comment-form',
                        isEditing: true,
                        id: siderConfig.id,
                        target: siderConfig.target,
                        pinpoint: comment.data?.pinpoint,
                      },
                    },
                    target: stackSiderView.target,
                  });
                },
              },
              {
                key: 'delete',
                label: t('delete'),
                danger: true,
                onClick: () => {
                  setDeletingCommentId(siderConfig.id);
                },
              },
            ]}
          />
        )}
      </Space>

      {openPublicationModalType && (
        // Modal内で定義しているuseFormのdefaultValuesをリセットするため
        <CommentPublicationModal
          onClose={handleCloseArchiveModal}
          comment={comment.data}
        />
      )}

      {hasEditPermission && (
        <ConfirmModal
          skipCheck
          open={!!deletingCommentId}
          title={t('delete-confirmation')}
          disclaimerText={t('delete-confirmation-disclaimer')}
          onOk={async () => {
            if (!deletingCommentId) {
              return;
            }

            try {
              await deleteComment(deletingCommentId);
              resetPinpoint();
              openMessage('success', t('message-delete-completed'));

              if (stackSiderView.list.length > 1) {
                stackSiderView.pop();
              } else {
                stackSiderView.clear();
              }
            } catch {
              openMessage('error', t('message-delete-failed'));
            }

            handleCloseDeleteModal();
          }}
          onCancel={handleCloseDeleteModal}
        />
      )}
    </>
  );
};
